/**
 * Subscription API - Simplified
 * Maps to Services-subscription/Service.api via Gateway
 * 
 * Industry Standard:
 * - All requests go through Gateway with httpOnly cookies
 * - No token management needed in frontend
 * - Single client handles all requests
 */

import apiClient from './client';
import { membershipsApi } from './organization';

// ----------------------------------------------------------------------------
// Subscription Types
// ----------------------------------------------------------------------------

export interface Subscription {
  id: string;
  churchId: string;
  tier: 'free' | 'basic' | 'founders' | 'pro' | 'enterprise';
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'trialing';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd?: boolean;
  trialEndsAt?: string;
  maxMembers?: number;
  maxPlans?: number;
  features?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  /**
   * Populated when the subscription was granted via the comp endpoint
   * (i.e. bypassed Stripe). Drives the gold/purple banner variants.
   * - `founder`  : early-adopter program (gold banner)
   * - `lifetime` : permanent free Ultimate access (royal purple banner)
   */
  compStatus?: {
    type: 'founder' | 'lifetime';
    grantedAt: string;
    grantedByEmail: string;
  };
  /**
   * Sticky founder flag. True once this church has redeemed a founder
   * promo code (or was migrated from the legacy `tier="founders"` cohort).
   * Renders a "Founders pricing locked-in" indicator and suppresses the
   * public Founders pricing card on the upgrade screen — the discount is
   * applied invisibly via Stripe coupon attached server-side.
   */
  isFounder?: boolean;
}

export interface Invoice {
  id: string;
  churchId: string;
  subscriptionId: string;
  stripeInvoiceId: string;
  amount: number;
  currency: string;
  status: string;
  paidAt: string | null;
  invoiceUrl: string;
  invoicePdf: string;
  createdAt: string;
}

export interface CheckoutRequest {
  churchId: string;
  tier: 'basic' | 'founders' | 'pro' | 'enterprise';
  successUrl?: string;
  cancelUrl?: string;
}

export interface CheckoutResponse {
  sessionId: string;
  checkoutUrl: string;
}

export interface PricingTier {
  name: string;
  tier: 'free' | 'basic' | 'founders' | 'pro' | 'enterprise';
  displayName: string;
  price: number;
  priceId?: string;
  maxMembers: number;
  maxTeams: number;
  maxPlans: number;
  features: string[];
  recommended?: boolean;
}

/**
 * Song Studio (AI chord detection) nav/page visibility. Basic and up — matches
 * the backend's GetTierFeatures paywall. Stem separation is Pro+ but is decided
 * server-side per upload, so it doesn't gate the page itself.
 */
export const canAccessChordAi = (subscription: Pick<Subscription, 'tier'> | null | undefined): boolean => {
  const tier = subscription?.tier;
  return tier === 'basic' || tier === 'founders' || tier === 'pro' || tier === 'enterprise';
};

/**
 * API keys (developer integrations) require Basic tier or above.
 */
export const canAccessApiKeys = (subscription: Pick<Subscription, 'tier'> | null | undefined): boolean => {
  const tier = subscription?.tier;
  return tier === 'basic' || tier === 'founders' || tier === 'pro' || tier === 'enterprise';
};

// ----------------------------------------------------------------------------
// API Functions
// ----------------------------------------------------------------------------

/**
 * Get subscription for a church
 * GET /api/subscriptions/church/{churchId}
 */
export const getSubscription = async (churchId: string): Promise<Subscription> => {
  const response = await apiClient.get(`/api/subscriptions/church/${churchId}`);
  return response.data;
};

/**
 * Tier entitlements for a church, readable by ANY signed-in user (the
 * subscription GET above is admin-only). Used by the member Song Studio to
 * decide between the upload UI and an upgrade prompt.
 * POST /api/subscriptions/check-limits
 */
export interface ChurchLimits {
  canUseChordAi: boolean;
  canUseAudioSeparation: boolean;
  currentTier: string;
}

export const checkChurchLimits = async (churchId: string): Promise<ChurchLimits> => {
  const response = await apiClient.post('/api/subscriptions/check-limits', { churchId });
  return {
    canUseChordAi: response.data?.canUseChordAi ?? true,
    canUseAudioSeparation: response.data?.canUseAudioSeparation ?? false,
    currentTier: response.data?.currentTier ?? 'unknown',
  };
};

/**
 * Verify a completed Stripe checkout session and create the subscription record
 * POST /api/subscriptions/verify-session/{sessionId}
 */
export const verifyCheckoutSession = async (sessionId: string): Promise<Subscription> => {
  const response = await apiClient.post(`/api/subscriptions/verify-session/${sessionId}`);
  return response.data;
};

/**
 * Create Stripe checkout session
 * POST /api/subscriptions/checkout
 */
export const createCheckoutSession = async (request: CheckoutRequest): Promise<CheckoutResponse> => {
  // Auto-include success/cancel URLs based on current origin
  const origin = window.location.origin;
  const enrichedRequest = {
    ...request,
    successUrl: request.successUrl || `${origin}/admin/subscription?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: request.cancelUrl || `${origin}/admin/subscription?checkout=canceled`,
  };
  const response = await apiClient.post('/api/subscriptions/checkout', enrichedRequest);
  return response.data;
};

/**
 * Cancel subscription
 * POST /api/subscriptions/cancel
 */
export const cancelSubscription = async (churchId: string, cancelImmediately: boolean = false): Promise<void> => {
  await apiClient.post('/api/subscriptions/cancel', { churchId, cancelImmediately });
};

/**
 * Create a free tier subscription for a new church
 * POST /api/subscriptions/create-free
 */
export const createFreeSubscription = async (churchId: string, onboardingToken?: string): Promise<Subscription> => {
  const config: any = {};
  if (onboardingToken) {
    config.headers = { Authorization: `Bearer ${onboardingToken}` };
  }
  const response = await apiClient.post('/api/subscriptions/create-free', { churchId }, config);
  return response.data;
};

/**
 * Get member count for a church (from Organizations API)
 */
export const getChurchMemberCount = async (churchId: string): Promise<number> => {
  const members = await membershipsApi.getChurchMembers(churchId);
  return members.length;
};

// ----------------------------------------------------------------------------
// Enterprise "Contact Sales" flow
//
// Enterprise is not self-serve — upgrade is handled manually via email.
// The Admin UI opens a mailto: link with a pre-filled subject/body instead
// of calling Stripe Checkout.
// ----------------------------------------------------------------------------

export const ENTERPRISE_CONTACT_EMAIL = 'support@churchnow.co';

export const buildEnterpriseContactMailto = (churchId?: string, currentTier?: string): string => {
  const subject = 'ChurchNow Enterprise Inquiry';
  const bodyLines = [
    'Hi ChurchNow team,',
    '',
    'We\'re interested in the Enterprise plan. Please get in touch about pricing and setup.',
    '',
    `Church ID: ${churchId ?? '(not provided)'}`,
    `Current plan: ${currentTier ?? 'free'}`,
    '',
    'Thanks!',
  ];
  const body = bodyLines.join('\n');
  return `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/**
 * Upgrade subscription
 * POST /api/subscriptions/upgrade
 */
export const upgradeSubscription = async (churchId: string, tier: 'basic' | 'founders' | 'pro' | 'enterprise'): Promise<void> => {
  await apiClient.post('/api/subscriptions/upgrade', { churchId, newTier: tier });
};

/**
 * Open the Stripe billing portal, where an admin updates their card or reviews
 * invoices.
 *
 * Plan changes no longer go through Stripe Checkout, so this is how a card gets
 * updated. Checkout only ever did it as a side effect of creating a second
 * subscription, which is what left customers billed for two plans at once.
 *
 * POST /api/subscriptions/billing-portal
 */
export const openBillingPortal = async (churchId: string): Promise<string> => {
  const response = await apiClient.post('/api/subscriptions/billing-portal', {
    churchId,
    returnUrl: window.location.href,
  });
  return response.data.portalUrl;
};

/**
 * Get invoices for a church
 * GET /api/subscriptions/invoices/{churchId}
 */
export const getInvoices = async (churchId: string): Promise<Invoice[]> => {
  const response = await apiClient.get(`/api/subscriptions/invoices/${churchId}`);
  return response.data;
};

// Pricing information.
// Team caps are set to keep read/write load sustainable per price tier
// (membership reads, roster writes, and team-scoped planning queries).
export const PRICING_TIERS: PricingTier[] = [
  {
    name: 'Free',
    tier: 'free',
    displayName: 'Free',
    price: 0,
    maxMembers: 5,
    maxTeams: 1,
    maxPlans: 3,
    features: [
      'Build setlists',
      'Share playlist links',
      '10-song library',
      'Up to 5 members',
      '3 active service plans',
      '1 group',
    ],
  },
  {
    name: 'Basic',
    tier: 'basic',
    displayName: 'Basic',
    price: 19,
    maxMembers: 50,
    maxTeams: 20,
    maxPlans: 999,
    features: [
      'Everything in Free',
      'AI chord detection (Song Studio)',
      'Advanced plan notifications',
      'Batch song import and CCLI support',
      'Service plan templates',
      'Up to 50 members',
      '20 groups',
      '200-song library',
      'Email support',
    ],
  },
  {
    name: 'Founders',
    tier: 'founders',
    displayName: 'ChurchNow Founders',
    price: 9.50,
    maxMembers: 200,
    maxTeams: 100,
    maxPlans: 999,
    features: [
      '$9.50/mo locked-in for life',
      'Everything in Pro',
      'AI stem separation',
      'Up to 200 members',
      '100 groups',
      '1000-song library',
      'Direct line to the founder',
      'Influence on the roadmap',
    ],
  },
  {
    name: 'Pro',
    tier: 'pro',
    displayName: 'Pro',
    price: 49,
    maxMembers: 200,
    maxTeams: 100,
    maxPlans: 999,
    features: [
      'Everything in Basic',
      'AI stem separation',
      'Real-time plan collaboration',
      'Up to 200 members',
      '100 groups',
      '1000-song library',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    displayName: 'Enterprise',
    price: 149,
    maxMembers: 999999,
    maxTeams: 999999,
    maxPlans: 999,
    features: [
      'Everything in Pro',
      'Unlimited members',
      'Unlimited resources',
      'Custom onboarding',
      'Dedicated support',
    ],
  },
];

// ----------------------------------------------------------------------------
// Founder Promo Codes (admin-only)
//
// Single-use Stripe promotion codes attached to the founder coupon. Restricted
// to comp-admins via the gateway-injected X-User-Email header allowlist on the
// server. The hooks below surface 401/403/503 from the API as thrown errors.
// ----------------------------------------------------------------------------

export interface FounderPromoCode {
  id: string;
  code: string;
  active: boolean;
  timesRedeemed: number;
  maxRedemptions: number | null;
  createdAt: string;
  expiresAt: string | null;
  couponId: string;
}

export interface MintFounderPromoCodeRequest {
  code?: string;
}

export const listFounderPromoCodes = async (): Promise<FounderPromoCode[]> => {
  const response = await apiClient.get('/api/subscriptions/founder-promo-codes');
  return response.data;
};

export const mintFounderPromoCode = async (
  body: MintFounderPromoCodeRequest = {}
): Promise<FounderPromoCode> => {
  const response = await apiClient.post('/api/subscriptions/founder-promo-codes', body);
  return response.data;
};

export const deactivateFounderPromoCode = async (id: string): Promise<FounderPromoCode> => {
  const response = await apiClient.delete(`/api/subscriptions/founder-promo-codes/${id}`);
  return response.data;
};
